import { useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/DataTable/index';
import { DataTableColumn } from '@/components/ui/DataTable/types';
import moment from 'moment-timezone';
import {
  useGetSnapshotByGuid,
  useGetOrderEventsByGuid,
  useListStagedEvents,
  TtOrderEvent,
  TtOrderSnapshot,
  TtOrderMetadata,
  OrderEventType,
} from '@/lib/api/risk/brokerage/tt';
import { RoutePath } from '@/section/routerPaths';
import { CommonDestinationTimezone } from '@/lib/utils';
import { AttachCustomerForm } from './AttachCustomerForm';
import { TTContractPreviewModal } from './TTContractPreviewModal';

export function TTOrderDetailView() {
  const { guid_order_id } = useParams<{ guid_order_id: string }>();
  const navigate = useNavigate();

  const decodedGuidOrderId = guid_order_id ? decodeURIComponent(guid_order_id) : '';

  const snapshotResult = useGetSnapshotByGuid(decodedGuidOrderId, {
    enabled: !!decodedGuidOrderId,
  });
  const eventsResult = useGetOrderEventsByGuid(decodedGuidOrderId, {
    enabled: !!decodedGuidOrderId,
  });
  const stagedResult = useListStagedEvents(decodedGuidOrderId, {
    enabled: !!decodedGuidOrderId,
  });
  // Contract preview modal state
  const [previewParams, setPreviewParams] = useState<{ eventId: string; guidOrderId: string } | null>(null);

  // Get the snapshot for this order directly from the API
  const snapshot: TtOrderSnapshot | null = snapshotResult.data?.data?.snapshot ?? null;

  // Get events for this order
  const allEvents: TtOrderEvent[] = eventsResult.data?.data?.events ?? [];

  // Filter state: default to hiding empty Updated events
  const [hideEmptyUpdates, setHideEmptyUpdates] = useState(true);

  // Filter out Updated events with no updated_working_quantity or updated_working_quantity === "0"
  const events = useMemo(() => {
    if (!hideEmptyUpdates) return allEvents;
    return allEvents.filter((event) => {
      if (event.event_type !== OrderEventType.Updated) return true;
      const qty = event.updated_working_quantity;
      return qty !== null && qty !== '0' && qty !== '';
    });
  }, [allEvents, hideEmptyUpdates]);

  // Get the first staged event for this order (filtered by guid_order_id on server)
  const stagedEvent = useMemo(() => {
    const staged = stagedResult.data?.data?.staged_events ?? [];
    return staged.length > 0 ? staged[0] : null;
  }, [stagedResult.data?.data?.staged_events]);

  // Get metadata from snapshot or staged event (similar to TTSnapshotOrdersTable)
  const existingMetadata: TtOrderMetadata | null = useMemo(() => {
    // Prefer snapshot metadata if available
    if (snapshot?.meta_data) {
      return snapshot.meta_data;
    }
    // Fall back to staged event metadata
    if (stagedEvent?.meta_data) {
      return stagedEvent.meta_data;
    }
    return null;
  }, [snapshot?.meta_data, stagedEvent?.meta_data]);

  const isLoading =
    snapshotResult.isLoading || eventsResult.isLoading || stagedResult.isLoading;

  const handleBack = () => {
    navigate(RoutePath.TTOrdersDashboard);
  };

  const handleRowClick = (event: TtOrderEvent, columnId: string) => {
    // Only process Filled events for HTA contracts with metadata
    if (event.event_type !== OrderEventType.Filled) {
      return;
    }

    if (!existingMetadata?.expected_hta) {
      return;
    }

    // Open the preview modal - it will handle fetching its own data
    setPreviewParams({
      eventId: event.id,
      guidOrderId: decodedGuidOrderId,
    });
  };

  const columns: DataTableColumn<TtOrderEvent>[] = useMemo(
    () => [
      {
        id: 'event_type',
        accessorKey: 'event_type',
        header: 'Event Type',
        size: 120,
        minSize: 100,
      },
      {
        id: 'event_time',
        accessorKey: 'event_time',
        header: 'Event Time',
        size: 180,
        minSize: 150,
        cell: (info) => {
          const value = info.getValue() as string | null;
          return value
            ? moment.tz(value, CommonDestinationTimezone).format('YYYY-MM-DD HH:mm:ss')
            : '';
        },
      },
      {
        id: 'buy_sell_type',
        accessorKey: 'buy_sell_type',
        header: 'Buy/Sell',
        size: 100,
        minSize: 80,
      },
      {
        id: 'order_quantity',
        accessorKey: 'order_quantity',
        header: 'Order Qty',
        size: 100,
        minSize: 80,
      },
      {
        id: 'working_quantity',
        accessorKey: 'working_quantity',
        header: 'Working Qty',
        size: 100,
        minSize: 80,
      },
      {
        id: 'filled_quantity',
        accessorKey: 'filled_quantity',
        header: 'Filled Qty',
        size: 100,
        minSize: 80,
      },
      {
        id: 'limit_price',
        accessorKey: 'limit_price',
        header: 'Limit Price',
        size: 120,
        minSize: 100,
      },
      {
        id: 'avg_fill_price',
        accessorKey: 'avg_fill_price',
        header: 'Avg Fill Price',
        size: 120,
        minSize: 100,
      },
      {
        id: 'message',
        accessorKey: 'message',
        header: 'Message',
        size: 200,
        minSize: 150,
        enableTooltip: true,
      },
      {
        id: 'reject_message',
        accessorKey: 'reject_message',
        header: 'Reject Message',
        size: 200,
        minSize: 150,
        enableTooltip: true,
      },
    ],
    []
  );

  if (!decodedGuidOrderId) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">Invalid order ID</p>
        <Button variant="outline" onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Back Button */}
      <div>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      {/* Order Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Order Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-20 bg-muted animate-pulse rounded" />
          ) : snapshot ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Instrument</p>
                <p className="font-medium">{snapshot.instrument_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Buy/Sell</p>
                <p className="font-medium">{snapshot.buy_sell_type || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Order Qty</p>
                <p className="font-medium">{snapshot.order_quantity ?? '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Remaining Qty</p>
                <p className="font-medium">{snapshot.remaining_quantity ?? '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Limit Price</p>
                <p className="font-medium">{snapshot.limit_price || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Time in Force</p>
                <p className="font-medium">{snapshot.time_in_force || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Account</p>
                <p className="font-medium">{snapshot.trading_account_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">GUID Order ID</p>
                <p className="font-mono text-sm break-all">{snapshot.guid_order_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Text A</p>
                <p className="font-medium">{snapshot.text_a || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Text B</p>
                <p className="font-medium">{snapshot.text_b || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Text C</p>
                <p className="font-medium">{snapshot.text_c || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Text TT</p>
                <p className="font-medium">{snapshot.text_tt || '-'}</p>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">No snapshot data found for this order</p>
          )}
        </CardContent>
      </Card>

      {/* Attach Customer Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Customer Assignment</CardTitle>
        </CardHeader>
        <CardContent>
          {(snapshot || stagedEvent) && (
            <AttachCustomerForm
              existingMetadata={existingMetadata}
              guidOrderId={decodedGuidOrderId}
              isInline
              totalQuantity={snapshot?.order_uom_quantity != null ? new Decimal(snapshot.order_uom_quantity) : null}
              orderTextFields={{
                textA: snapshot?.text_a ?? stagedEvent?.order_event?.text_a,
                textB: snapshot?.text_b ?? stagedEvent?.order_event?.text_b,
                textC: snapshot?.text_c ?? stagedEvent?.order_event?.text_c,
                textTT: snapshot?.text_tt ?? stagedEvent?.order_event?.text_tt,
              }}
            />
          )}
          {stagedEvent && !stagedEvent.has_associated_metadata && !existingMetadata && (
            <p className="text-sm text-orange-600 mt-2">
              No meta data associated yet
            </p>
          )}
        </CardContent>
      </Card>

      {/* Order Events Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Order Events ({events.length})</CardTitle>
          <Button
            variant={hideEmptyUpdates ? 'default' : 'outline'}
            size="sm"
            onClick={() => setHideEmptyUpdates(!hideEmptyUpdates)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {hideEmptyUpdates ? 'Show All Updates' : 'Hide Other Updates'}
          </Button>
        </CardHeader>
        <CardContent>
          {eventsResult.isLoading ? (
            <div className="p-4">
              <p>Loading events...</p>
            </div>
          ) : eventsResult.isError ? (
            <div className="p-4">
              <p className="text-red-500">Error loading events</p>
            </div>
          ) : (
            <DataTable
              data={events}
              columns={columns}
              enableSorting
              enableMultiSort
              enableFiltering
              enableColumnVisibility
              enableExport
              emptyMessage="No events found for this order"
              stickyHeaders
              tableClassName="max-h-[400px] overflow-y-scroll"
              defaultSorting={[{ id: "event_time", desc: true }]}
              rowClickable
              onRowClick={handleRowClick}
            />
          )}
        </CardContent>
      </Card>

      {/* Contract Preview Modal */}
      {previewParams && (
        <TTContractPreviewModal
          eventId={previewParams.eventId}
          guidOrderId={previewParams.guidOrderId}
          onClose={() => setPreviewParams(null)}
        />
      )}
    </div>
  );
}
